import React, { useState, useEffect } from 'react';
import './App.css';
import groupPic from './assets/group-pic.JPG';
import linkedinIcon from './assets/linkedin-icon.png';
import invertedDiagram from './assets/inverted-diagram.png';
import invertedDiagramBlue from './assets/inverted-diagram-blue.png';
import garminInverted from './assets/garmin-inverted.png';
import configsc1 from './assets/app_screenshots/config1.PNG';
import configsc2 from './assets/app_screenshots/config2.PNG';
import configsc3 from './assets/app_screenshots/config3.PNG';
import historysc from './assets/app_screenshots/history-screen.PNG';
import metricsc from './assets/app_screenshots/metric-screen.PNG';
import reactLogo from './assets/icons8-react-native-48.png';
import gitHubLogo from './assets/github-blue.png';

function App() {
  const [isBlueDiagram, setIsBlueDiagram] = useState(false);
  const [isButtonPressed, setIsButtonPressed] = useState(false);
  const [activeSection, setActiveSection] = useState('introduction');

  const toggleDiagram = () => {
    setIsBlueDiagram(!isBlueDiagram);
    setIsButtonPressed(!isButtonPressed);
  };

  useEffect(() => {
    const sections = document.querySelectorAll('section');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.2 }
    );

    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="App">
      <nav className="navbar">
        <ul>
          <li className={activeSection === 'introduction' ? 'active' : ''}>
            <a href="#introduction">Introduction</a>
          </li>
          <li className={activeSection === 'project-overview' ? 'active' : ''}>
            <a href="#project-overview">Project Overview</a>
          </li>
          <li className={activeSection === 'meet-the-team' ? 'active' : ''}>
            <a href="#meet-the-team">Meet the Team</a>
          </li>
        </ul>
      </nav>

      <section id="introduction" className="parallax">
        <div className="header-container">
          <img src={garminInverted} alt="Garmin Logo" className="inline-logo" />
          <h1 className="project-header">RoboRacer</h1>
        </div>
        <div className="animated-box">
          <p className="box-text">Robot Image Coming Soon</p>
        </div>
        <div className="description">
          <p>
            The RoboRacer is a robot designed to replace human pacers. It provides an accurate and configurable way for athletes to train. Made in collaboration with Garmin™.
          </p>
        </div>
      </section>

      <section id="project-overview">
        <h2>Project Overview</h2>
        <p>
          The RoboRacer was a collaborative project between a CS team and an ECE team. This overview focuses on the{' '}
          <button
            className={`inline-toggle-button ${isButtonPressed ? 'pressed' : ''}`}
            onClick={toggleDiagram}
          >
            CS team's contributions
          </button>
          .
        </p>
        <div className="diagram-container">
          <img
            src={isBlueDiagram ? invertedDiagramBlue : invertedDiagram}
            alt="Inverted Diagram"
            className="diagram-image"
          />
        </div>

        <div className="app-development">
          <h2 className="main-header">App Development</h2>
          <div className="app-built-with">
            <div className="header-with-logo">
              <h3>Built with React Native</h3>
              <img src={reactLogo} alt="React Native Logo" className="react-logo" />
            </div>
            <p>
              The RoboRacer app was developed using React Native, enabling cross-platform compatibility and a seamless user experience on both iOS and Android devices.
            </p>
          </div>
        </div>

        <div className="section-container">
          <div className="section-header">
            <h3>Configuring the Robot</h3>
            <p>
              Once connected via Bluetooth, the RoboRacer app allows users to configure the robot's speed and distance intervals. Additionally, save these configurations to your Workout Profile for easy future use.
            </p>
          </div>
          <div className="section-screenshots">
            <img src={configsc1} alt="Configuring Screenshot 1" className="screenshot" />
            <img src={configsc2} alt="Configuring Screenshot 2" className="screenshot" />
            <img src={configsc3} alt="Configuring Screenshot 3" className="screenshot" />
          </div>
        </div>

        <div className="section-container">
          <div className="section-header">
            <h3>Saving Workout History</h3>
            <p>
              Workout history is automatically saved in order to track progress over time and revisit past sessions.
            </p>
          </div>
          <div className="section-screenshots">
            <img src={historysc} alt="Saving Workout History Screenshot" className="screenshot" />
          </div>
        </div>

        <div className="section-container">
          <div className="section-header">
            <h3>Viewing Workout Metrics</h3>
            <p>View detailed metrics from your workouts based on speed and distance.</p>
          </div>
          <div className="section-screenshots">
            <img src={metricsc} alt="Viewing Workout Metrics Screenshot" className="screenshot" />
          </div>
        </div>


        <div className="embedded-systems">
          <h2 className="main-header">Embedded Systems</h2>
        </div>

        <div className="section-container">
          <div className="section-header">
            <h3>Autonomous Capability</h3>
            <p>
              The RoboRacer robot contains a STM32 Nucleo on-board, responsible for taking user-generated routines from the mobile app and automatically generating custom speed routines. The customized operating system built with FreeRTOS allows the robot to systematically adjust this speed routine to account for obstacles and unexpected scenarios autonomously, allowing for a smooth and worry-free training experience.
            </p>
          </div>
          <div className="section-screenshots">
            <div className="animated-box">
            <p className="box-text">Robot Image Coming Soon</p>
            </div>
          </div>
        </div>


      </section>

  

      <section id="meet-the-team">
        <h2>Meet the Team</h2>
        <p>The CS team behind RoboRacer.</p>
        <div className="team-container">
          <div className="team-photo-container">
            <img src={groupPic} alt="Team" className="team-image" />
            <p className="team-caption">Left to right: Steven Sarber, Faaizah Nuha, Stella Doehring, Rahul Kannan</p>
          </div>

          <div className="team-members">
            <div className="team-member">
              <div className="team-header">
                <h3>Steven Sarber</h3>
                <a href="https://www.linkedin.com/in/steven-sarber-06b573255/" target="_blank" rel="noopener noreferrer">
                  <img src={linkedinIcon} alt="LinkedIn" className="linkedin-icon" />
                </a>
              </div>
              <hr />
              <p>CS undergrad with a focus in Computer Systems.</p>
              <hr />
            </div>

            <div className="team-member">
              <div className="team-header">
                <h3>Faaizah Nuha</h3>
                <a href="https://www.linkedin.com/in/faaizah-nuha/" target="_blank" rel="noopener noreferrer">
                  <img src={linkedinIcon} alt="LinkedIn" className="linkedin-icon" />
                </a>
              </div>
              <hr />
              <p>CS undergrad with a focus in Computer Systems, incoming Master's student at Georgia Tech.</p>
              <hr />
            </div>

            <div className="team-member">
              <div className="team-header">
                <h3>Stella Doehring</h3>
                <a href="https://www.linkedin.com/in/stella-doehring-12bb4b339/" target="_blank" rel="noopener noreferrer">
                  <img src={linkedinIcon} alt="LinkedIn" className="linkedin-icon" />
                </a>
              </div>
              <hr />
              <p>CS undergrad with a focus in AI, Accelerated Master's student at Oregon State University.</p>
              <hr />
            </div>

            <div className="team-member">
              <div className="team-header">
                <h3>Rahul Kannan</h3>
                <a href="https://www.linkedin.com/in/rahul-kannan-a60609204/" target="_blank" rel="noopener noreferrer">
                  <img src={linkedinIcon} alt="LinkedIn" className="linkedin-icon" />
                </a>
              </div>
              <hr />
              <p>CS undergrad with a focus in Computer Systems.</p>
              <hr />
            </div>
          </div>
        </div>

        <div className="acknowledgements">
          <h2>Acknowledgements</h2>
          <p>
            This project began last year, with the original CS Team. 
            While we didn't pick up exactly where their work left off, 
            we still wish to acknowledge them and their contributions as a starting point to this project. 
            Their documentation and GitHub are linked below.
          </p>
              <a href="https://github.com/Robo-Racer" target="_blank" rel="noopener noreferrer">
                <img src={gitHubLogo} alt="gitHub" className="github-icon" />
              </a>
            <a
              href="https://github.com/Robo-Racer"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#61dafb" }}
            >
              Documentation Website
            </a>
        </div>
      </section>
    </div>
  );
}

export default App;